import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input, Select, Text, useToast } from "components-lib";
import { axiosInstance } from "config/api";
import EnhancedAutocompleteInput from "../EnhancedAutocompleteInput";
import { useDoctorRecommendations } from "../hooks/useDoctorRecommendations";
import {
  saveToStorage,
  getStoredSuggestions,
  STORAGE_KEYS,
} from "../utils/storageUtils";
import { cachedApiCall, CACHE_KEYS } from "utils/apiCache";

/**
 * AdviceSection Component
 * Manages patient advice with dropdown selection and custom advice functionality
 * Integrates with API for fetching and adding advice entries
 * Provides enhanced autocomplete functionality with API integration
 */
/*
  Note: AdviceSection receives `templateData` and in a useEffect it normalizes
  `templateData.advices` (or `advice_text`) and appends unique entries into
  `advicesData` via `setAdvicesData`.

  See `docs/LOAD_TEMPLATE_FLOW.md` for the overall flow.
*/
const AdviceSection = ({
  templateData,
  advicesData,
  setAdvicesData,
  newAdvice,
  setNewAdvice,
  selectedAdviceId,
  setSelectedAdviceId,
  adviceSuggestions,
  setAdviceSuggestions,
  doctor_id,
  appointmentId,
  appointmentPrescription,
}) => {
  // Use the doctor recommendations hook for API-based suggestions
  const { recommendations } = useDoctorRecommendations(doctor_id);
  const toast = useToast();

  const hydratedFromPrescriptionRef = useRef(null);

  // Local state for API advice data
  const [apiAdvices, setApiAdvices] = useState([]);
  const [isLoadingAdvices, setIsLoadingAdvices] = useState(false);



  // Fetch advice data from API with caching
  const fetchAdvicesFromAPI = useCallback(async () => {
    const doctorId = doctor_id;
    if (!doctorId) {
      console.warn("[AdviceSection] No doctor ID provided as prop");
      setApiAdvices([]);
      return;
    }

    setIsLoadingAdvices(true);
    try {
      const cacheKey = CACHE_KEYS.DOCTOR_ADVICES(doctorId);

      const advicesArray = await cachedApiCall(
        cacheKey,
        async () => {
          console.log(`[AdviceSection] Fetching advices from API for doctor ${doctorId}`);
          const response = await axiosInstance.get(
            `/api/prescription/getAdvices?doctor_id=${doctorId}`
          );

          let result = [];
          if (response.data?.data && Array.isArray(response.data.data)) {
            result = response.data.data;
          } else if (Array.isArray(response.data)) {
            result = response.data;
          }
          return result;
        },
        { ttl: 5 * 60 * 1000 } // 5 minute cache
      );

      setApiAdvices(advicesArray);

      // Save full advice list to localStorage for PreviewPage ID recovery
      try {
        localStorage.setItem('doctor_advices_full_list', JSON.stringify(advicesArray));
      } catch (e) {
        console.warn('Failed to save doctor advices to localStorage:', e);
      }



      console.log(`[AdviceSection] Loaded ${advicesArray.length} advices`);
    } catch (error) {
      console.error("[AdviceSection] Error fetching advice data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch advice data",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingAdvices(false);
    }
  }, [doctor_id, toast]);

  // Handle advice selection from autocomplete
  const handleAdviceSelect = (value, info) => {
    setNewAdvice((prev) => ({
      ...prev,
      advice: value,
      id: info?.metadata?.id || null, // Set the ID from metadata for translations
    }));

    // Save to localStorage for future suggestions
    saveToStorage(STORAGE_KEYS.ADVICE, value);
  };

  // Handle advice input change
  const handleAdviceChange = (e) => {
    const value = e.target.value;
    setNewAdvice((prev) => ({
      ...prev,
      advice: value,
    }));
  };

  // Handle advice dropdown selection
  const handleAdviceSelection = async (e) => {
    const selectedId = e.target.value;
    setSelectedAdviceId(selectedId);

    if (selectedId === "custom") {
      // Clear advice text for custom input
      setNewAdvice({
        ...newAdvice,
        advice: "",
        id: null,
        translations: null,
      });
    } else if (selectedId) {
      const selectedAdvice = apiAdvices.find(
        (advice) => advice.id.toString() === selectedId
      );
      if (selectedAdvice) {
        setNewAdvice({
          ...newAdvice,
          advice: selectedAdvice.advice_text,
          id: selectedAdvice.id,
          translations: null,
        });
      }
    } else {
      // If no selection, clear the advice text
      setNewAdvice({
        ...newAdvice,
        advice: "",
        id: null,
        translations: null,
      });
    }
  };

  // Add advice handler
  const handleAddAdvice = async () => {
    const adviceText = newAdvice?.advice?.toString?.() || "";
    if (adviceText.trim()) {
      let adviceToAdd = {
        ...newAdvice,
        advice: adviceText, // Ensure advice is a string
        id: (selectedAdviceId && selectedAdviceId !== "custom") ? selectedAdviceId : newAdvice.id,
      };

      // If it's a custom advice (selectedAdviceId === "custom"), make API call
      // if (selectedAdviceId === "custom") {
      //   try {
      //     // Get current doctor ID from localStorage
      //     const userData = JSON.parse(localStorage.getItem("user") || "{}");
      //     const tokenPayload = JSON.parse(
      //       localStorage.getItem("tokenPayload") || "{}"
      //     );
      //     const doctorId =
      //       userData?.id || userData?.doctorId || tokenPayload?.userId || null;

      //     const response = await axiosInstance.post(
      //       "/api/prescription/addAdvice",
      //       {
      //         advice_text: newAdvice.advice,
      //         doctor_id: doctorId, // Use dynamic doctor ID
      //         translations: {}, // Empty translations as requested
      //       }
      //     );

      //     console.log("Custom advice added successfully:", response.data);

      //     // Update the advice with the returned ID from API
      //     if (response.data && response.data.id) {
      //       adviceToAdd.id = response.data.id;
      //     }

      //     toast({
      //       title: "Success",
      //       description: "Custom advice added successfully",
      //       status: "success",
      //       duration: 3000,
      //       isClosable: true,
      //     });

      //     // Refresh the advice list to include the newly added custom advice
      //     fetchAdvicesFromAPI();
      //   } catch (error) {
      //     console.error("Error adding custom advice:", error);
      //     toast({
      //       title: "Error",
      //       description: "Failed to add custom advice",
      //       status: "error",
      //       duration: 3000,
      //       isClosable: true,
      //     });
      //     return; // Don't add to local state if API call failed
      //   }
      // }

      setAdvicesData([...advicesData, adviceToAdd]);
      setNewAdvice({
        advice: "",
        details: "",
        date: new Date().toISOString().split("T")[0],
        id: null,
      });
      setSelectedAdviceId(""); // Reset dropdown selection
    }
  };

  // Delete advice handler
  const handleDeleteAdvice = (index) => {
    setAdvicesData(advicesData.filter((_, i) => i !== index));
  };

  useEffect(() => {
    fetchAdvicesFromAPI();
  }, [doctor_id, fetchAdvicesFromAPI]);

  useEffect(() => {
    if (!appointmentId || !appointmentPrescription) return;
    if (hydratedFromPrescriptionRef.current === appointmentId) return;

    try {
      const source = appointmentPrescription.data || appointmentPrescription;
      const apiAdvices = Array.isArray(source?.advice)
        ? source.advice
        : Array.isArray(source?.advices)
          ? source.advices
          : Array.isArray(source?.prescription?.advice)
            ? source.prescription.advice
            : [];

      const idsToCache = [];

      apiAdvices.forEach((apiAdvice) => {
        const adviceText = apiAdvice?.advice_text || apiAdvice?.advice;
        if (!adviceText) return;

        const exists = advicesData.some(
          (existingAdvice) =>
            (existingAdvice.advice || "").toString().toLowerCase() ===
            adviceText.toString().toLowerCase(),
        );

        if (!exists) {
          const newAdviceEntry = {
            advice: adviceText.trim(),
            id: apiAdvice.id,
            date: apiAdvice.date,
          };

          setAdvicesData((prevAdvices = []) => [...prevAdvices, newAdviceEntry]);
          saveToStorage(STORAGE_KEYS.ADVICE, adviceText.trim());
        }

        if (apiAdvice.id) idsToCache.push(apiAdvice.id);
      });



      hydratedFromPrescriptionRef.current = appointmentId;
    } catch (error) {
      console.error(`Error hydrating advices for appointment ${appointmentId}:`, error);
    }
  }, [appointmentId, appointmentPrescription, advicesData, setAdvicesData]);

  useEffect(() => {
    if (!templateData) return;

    try {
      let parsed = templateData;
      if (typeof templateData === "string") {
        parsed = JSON.parse(templateData);
      }

      const templateAdvices = Array.isArray(parsed.advices)
        ? parsed.advices
        : Array.isArray(parsed)
        ? parsed
        : [];

      const normalized = templateAdvices
        .map((adv) => ({
          advice: (adv.advice ?? adv.advice_text ?? "").toString().trim(),
          id: adv.id ?? null,
          date: adv.date ?? adv.created_at ?? null,
          translations: adv.translations ?? null,
        }))
        .filter((a) => a && a.advice);

      if (normalized.length === 0) return;

      setAdvicesData((prev = []) => {
        const existing = Array.isArray(prev) ? prev : [];
        const existingSet = new Set(
          existing.map((e) => (e.advice || "").toString().toLowerCase())
        );

        const toAdd = normalized.filter(
          (n) => !existingSet.has(n.advice.toLowerCase())
        );

        if (toAdd.length === 0) return existing;

        toAdd.forEach((n) => saveToStorage(STORAGE_KEYS.ADVICE, n.advice));

        return [...existing, ...toAdd];
      });
    } catch (err) {
      console.warn("Failed to parse or apply template advices:", err);
    }
  }, [templateData, setAdvicesData]);

  return (
    <tr>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        <Text fontWeight="bold" style={{ fontSize: "16px" }}>
          Advice:
        </Text>
      </td>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        {/* Combined Advice Dropdown and Custom Input */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "15px",
          }}
        >
          <Select
            value={selectedAdviceId}
            onChange={handleAdviceSelection}
            placeholder={
              isLoadingAdvices
                ? "Loading advice..."
                : "Select an advice or choose 'Custom'"
            }
            disabled={isLoadingAdvices}
            style={{
              width: "70%",
              padding: "5px 30px 5px 5px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          >
            {apiAdvices.map((advice) => (
              <option key={advice.id} value={advice.id}>
                {advice.advice_text}
              </option>
            ))}
            <option value="custom">+ Add Custom Advice</option>
          </Select>

          {selectedAdviceId === "custom" && (
            <Input
              value={newAdvice.advice}
              onChange={(e) =>
                setNewAdvice({ ...newAdvice, advice: e.target.value })
              }
              placeholder="Enter custom advice"
              style={{
                width: "100%",
                padding: "5px",
                borderRadius: "5px",
                border: "1px solid #ccc",
              }}
            />
          )}

          <button
            style={{
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              padding: "5px 10px",
              fontSize: "12px",
              whiteSpace: "nowrap",
            }}
            onClick={handleAddAdvice}
            disabled={!newAdvice?.advice?.toString?.()?.trim()}
          >
            Add
          </button>
        </div>

        {/* Enhanced Autocomplete Input for Advice Search */}
        <div style={{ marginTop: "10px" }}>
          <EnhancedAutocompleteInput
            value={newAdvice.advice}
            onChange={handleAdviceChange}
            onSelect={handleAdviceSelect}
            placeholder="Enter advice..."
            suggestions={[
              ...recommendations.advices,
              ...getStoredSuggestions(STORAGE_KEYS.ADVICE),
            ]}
            allowCustomInput={true}
            maxSuggestions={8}
            minInputLength={1}
            highlightMatch={true}
            onCustomInput={(value) => saveToStorage(STORAGE_KEYS.ADVICE, value)}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #E2E8F0",
            }}
          />
        </div>

        {/* Selected Advices Container */}
        {advicesData.length > 0 && (
          <div
            style={{
              border: "none",
              borderRadius: "10px",
              padding: "12px",
              backgroundColor: "#F8FAFC",
              marginTop: "12px",
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
            }}
          >
            <Text
              fontWeight="bold"
              style={{ fontSize: "14px", marginBottom: "8px" }}
            >
              Selected Advices:
            </Text>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {advicesData.map((advice, index) => (
                <div
                  key={index}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    backgroundColor: "#E2E8F0",
                    borderRadius: "12px",
                    padding: "8px 12px",
                    fontSize: "14px",
                    gap: "8px",
                  }}
                >
                  <span>{advice.advice}</span>
                  <button
                    style={{
                      backgroundColor: "#CBD5E1",
                      color: "red",
                      fontWeight: "bold",
                      fontSize: "14px",
                      border: "none",
                      borderRadius: "12px",
                      cursor: "pointer",
                      padding: "4px 8px",
                      lineHeight: "1",
                      minWidth: "20px",
                      height: "20px",
                    }}
                    onClick={() => handleDeleteAdvice(index)}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </td>
    </tr>
  );
};

export default AdviceSection;
